import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import {
  Insurance,
  PatientStatus,
  PatientVisit,
  Provider,
  UserCoachScreeningData,
  VISIT_TYPES,
  VisitType,
} from "~/services";
import { TimeSlot } from "~/shared.types";
import { safeStorage } from "~/shared/utils/safeStorage";

export type AppointmentDetails = {
  provider: Provider | null;
  timeSlot: TimeSlot | null;
  timezone: string;
  date: string;
  formattedDate: string;
  formattedTime: string;
  visitDateTime: string;
};

type PatientState = {
  visitType: VisitType;
  setVisitType: (visitType: VisitType) => void;

  patientType: PatientStatus | null;
  setPatientType: (type: PatientStatus | null) => void;

  selectedProvider: number | null;
  setSelectedProvider: (providerId: number | null) => void;

  meetCoachData: UserCoachScreeningData | null;
  setMeetCoachData: (data: UserCoachScreeningData) => void;
  clearMeetCoachData: () => void;

  appointmentDetails: AppointmentDetails;
  setAppointmentDetails: (details: Partial<AppointmentDetails>) => void;
  clearAppointmentDetails: () => void;

  patientVisitData: Partial<PatientVisit> | null;
  updatePatientVisitData: (data: Partial<PatientVisit>) => void;
  clearPatientVisitData: () => void;
  getPatientVisitData: () => Partial<PatientVisit> | null;
  insuranceData: Insurance | null;
  clearInsuranceData: () => void;
  setInsuranceData: (data: Insurance | null) => void;

  insuranceNotListed: boolean;
  setInsuranceNotListed: (value: boolean) => void;

  hasFileSizeError: boolean;
  setHasFileSizeError: (value: boolean) => void;
  clearHasFileSizeError: () => void;

  isRescheduling: boolean;
  setIsRescheduling: (value: boolean) => void;

  clearStore: () => void;
};

export const usePatientStore = create<PatientState>()(
  persist(
    (set, get) => ({
      visitType: VISIT_TYPES.INITIAL,
      setVisitType: (visitType) => set({ visitType }),

      patientType: null,
      setPatientType: (type) => set({ patientType: type }),

      selectedProvider: null,
      setSelectedProvider: (providerId) =>
        set({ selectedProvider: providerId }),

      meetCoachData: null,
      setMeetCoachData: (data) => set({ meetCoachData: data }),
      clearMeetCoachData: () => set({ meetCoachData: null }),
      appointmentDetails: {
        provider: null,
        timeSlot: null,
        timezone: "",
        date: "",
        formattedDate: "",
        formattedTime: "",
        visitDateTime: "",
      },
      setAppointmentDetails: (details) =>
        set((state) => ({
          appointmentDetails: { ...state.appointmentDetails, ...details },
        })),
      clearAppointmentDetails: () =>
        set({
          appointmentDetails: {
            provider: null,
            timeSlot: null,
            timezone: "",
            date: "",
            formattedDate: "",
            formattedTime: "",
            visitDateTime: "",
          },
        }),

      patientVisitData: null,
      updatePatientVisitData: (data) =>
        set((state) => ({
          patientVisitData: { ...(state.patientVisitData || {}), ...data },
        })),
      clearPatientVisitData: () => set({ patientVisitData: null }),
      getPatientVisitData: () => get().patientVisitData,

      insuranceData: null,
      setInsuranceData: (data) => set({ insuranceData: data }),
      clearInsuranceData: () =>
        set({ insuranceData: null, insuranceNotListed: false }),

      insuranceNotListed: false,
      setInsuranceNotListed: (value) => set({ insuranceNotListed: value }),

      hasFileSizeError: false,
      setHasFileSizeError: (value) => set({ hasFileSizeError: value }),
      clearHasFileSizeError: () => set({ hasFileSizeError: false }),

      isRescheduling: false,
      setIsRescheduling: (value) => set({ isRescheduling: value }),

      clearStore: () =>
        set({
          visitType: VISIT_TYPES.INITIAL,
          patientType: null,
          selectedProvider: null,
          meetCoachData: null,
          appointmentDetails: {
            provider: null,
            timeSlot: null,
            timezone: "",
            date: "",
            formattedDate: "",
            formattedTime: "",
            visitDateTime: "",
          },
          patientVisitData: null,
          insuranceData: null,
          insuranceNotListed: false,
          hasFileSizeError: false,
          isRescheduling: false,
        }),
    }),
    {
      name: "patient-store",
      storage: createJSONStorage(() => safeStorage),
    },
  ),
);
